import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmModal from '../components/ui/ConfirmModal';
import { ModalCloseGuardContext } from '../components/ui/IOSModal';

/**
 * Protegge un form in un modal dalla chiusura con modifiche non salvate.
 *
 * - `value`: lo stato del form da confrontare (serializzato in JSON).
 * - Il "punto pulito" (baseline) viene preso da solo alla prima render con `isOpen && ready`;
 *   se il form carica i dati in un effect, passare `ready: false` e chiamare `setBaseline(datiCaricati)`.
 * - `requestClose()` chiude subito se non ci sono modifiche, altrimenti chiede conferma.
 * - Dentro un IOSModal si registra da solo: Esc e la X dell'IOSModal passano da qui.
 * - Con modifiche in corso il browser avvisa anche alla chiusura/ricarica della pagina.
 * - `confirmDialog` va renderizzato dal form (è in un portal, sopra anche allo schermo intero).
 */
export default function useUnsavedChangesGuard({ isOpen, value, onClose, ready = true }) {
    const [baseline, setBaselineState] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const current = JSON.stringify(value);
    const isDirty = Boolean(isOpen) && baseline !== null && current !== baseline;

    const setBaseline = useCallback((v) => setBaselineState(JSON.stringify(v)), []);

    useEffect(() => {
        if (!isOpen) {
            setBaselineState(null);
            setConfirmOpen(false);
        } else if (ready && baseline === null) {
            setBaselineState(current);
        }
    }, [isOpen, ready, baseline, current]);

    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const requestClose = useCallback(() => {
        if (isDirtyRef.current) {
            setConfirmOpen(true);
        } else {
            onCloseRef.current?.();
        }
    }, []);

    useEffect(() => {
        if (!isDirty) return undefined;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const modalGuard = useContext(ModalCloseGuardContext);
    useEffect(() => {
        if (!modalGuard || !isOpen) return undefined;
        modalGuard.setGuard(requestClose);
        return () => modalGuard.setGuard(null);
    }, [modalGuard, isOpen, requestClose]);

    const confirmDialog = typeof document !== 'undefined'
        ? createPortal(
            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    setConfirmOpen(false);
                    onCloseRef.current?.();
                }}
                title="Modifiche non salvate"
                message="Hai delle modifiche non salvate. Se esci adesso le perdi."
                confirmText="Esci senza salvare"
                cancelText="Continua a modificare"
                variant="warning"
                zIndexClass="z-[10000]"
            />,
            document.body,
        )
        : null;

    return { isDirty, requestClose, setBaseline, confirmDialog };
}
